import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { financeApi } from '../../services/financeApi';
import { ProofException, ProofNumber, ProofRunResponse, ProofSide, PROOF_TITLES } from '../../types/proofs';
import Money, { formatAed } from './Money';
import { GlossaryKey, isGlossaryKey } from './glossary';
import { ProofStatusBadge } from './PositionCards';

const RAN_AT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/**
 * The stored INCONCLUSIVE reason, in words. The reason is a machine constant (`NO_OPENING_SNAPSHOT`),
 * and the drawer used to print it verbatim — #547 needs the newest one to tell an auditor what
 * actually happened, so each gets a sentence and an unmapped reason still falls back to the raw
 * constant rather than disappearing.
 */
const REASON_TEXT: Record<string, string> = {
  BILLING_UNAVAILABLE: 'the billing service could not be reached, so the Stripe side of the check is missing. No alert fired; re-run when billing is up.',
  BILLING_REJECTED_REQUEST: 'billing rejected the request for this window — that is a bug here rather than an outage, and it is logged at ERROR.',
  NO_OPENING_SNAPSHOT: 'there is no Stripe balance snapshot at or after ledger go-live, so the window has no starting point. Take one with Proofs → Run for today, or Stripe Payouts → Sync now.',
  NO_CLOSING_SNAPSHOT: 'no Stripe balance snapshot exists after the opening one, so the window has no end point yet.',
  OPENING_SNAPSHOT_AFTER_WINDOW: 'ledger go-live falls after this day’s last balance snapshot, so no custody reading exists inside the day yet. It self-heals on the next run.',
  OPENING_SNAPSHOT_BOOTSTRAPPED: 'this run took the very first Stripe balance snapshot, so it is the start of the window rather than a measurement of one — there was nothing to compare. Run the proofs again later and that run measures the window between the two readings.',
  ERROR: 'the proof threw before it could compute; the error is on the left-hand side.',
};

/** Glossary key for each proof's headline variance. */
export const PROOF_VARIANCE_KEY: Record<ProofNumber, GlossaryKey> = {
  1: 'proof1Variance',
  2: 'proof2Variance',
  3: 'proof3Variance',
};

/**
 * lhs/rhs field → glossary key. Fields are the stored JSON of `ledger_proof_runs`, flattened with
 * dots (`awaitingPayment.total`). A field with no entry here still renders, but as plain text —
 * the ⓘ only appears for figures the glossary explains.
 */
export const FIELD_KEYS: Record<ProofNumber, Record<string, GlossaryKey>> = {
  1: {
    'awaitingPayment.total': 'netPayable',
    'balance2150': 'proof1Balance2150',
    'balance2130': 'proof1Balance2130',
    'unexplained2150': 'proof1Balance2150',
    'unexplained2130': 'proof1Balance2130',
  },
  2: {
    'ledgerCustodyDelta': 'proof2LedgerCustodyDelta',
    'stripeDelta': 'proof2StripeDelta',
    'payoutsCreated.total': 'proof2PayoutsCreated',
    'payoutsCreated': 'proof2PayoutsCreated',
    'garageTransfers': 'proof2GarageTransfers',
    'ownFundsWithdrawn': 'proof2OwnFundsWithdrawn',
    'goodwillFromCustody': 'proof2GoodwillFromCustody',
    'disputesDebited.total': 'proof2DisputesDebited',
    'disputesDebited': 'proof2DisputesDebited',
    // #544 — the won-dispute credit back and the net movement that enters the equation.
    'disputesReversed.total': 'proof2DisputesReversed',
    'disputesReversed': 'proof2DisputesReversed',
    'disputesNet': 'proof2DisputesNet',
    'excluded.feeOnly.amount': 'proof2ExcludedFeeOnly',
    'excluded.nonStripe.amount': 'proof2ExcludedNonStripe',
    'variance': 'proof2Variance',
  },
  3: {
    'documentVat': 'proof3DocumentVat',
    'expectedVat': 'proof3ExpectedVat',
    'ledgerOutputVat': 'proof3LedgerOutputVat',
    'variance': 'proof3Variance',
  },
};

/**
 * Rows that are a CAPTION on another row rather than a row of their own: `figure key → caption key`.
 *
 * `ledger_proof_runs.lhs/rhs` is **jsonb**, which normalises key order ("shortest key first, then
 * bytewise"), so the order `Proof1PayoutIntegrity` writes its `LinkedHashMap` in does not survive
 * persistence — `balance2130Since` comes back as the LAST row of the table, five rows below the
 * figure it qualifies (#588). Decision 8's constraint is that the 2130 balance is never rendered as
 * a bare all-time-looking number, so the qualifier is attached to the figure's own cell instead of
 * being a sibling row whose position the payload decides. The figure's label is unchanged.
 *
 * A caption row is only absorbed when its figure is present in the same side, so an unexpected
 * payload can never make the value disappear.
 */
export const ROW_CAPTIONS: Record<ProofNumber, Record<string, string>> = {
  1: { 'balance2130': 'balance2130Since' },
  2: {},
  3: {},
};

/** Exception amount → glossary key by the row type the backend names. */
export function exceptionGlossaryKey(proof: ProofNumber, type: string): GlossaryKey {
  const t = String(type).toUpperCase();
  if (proof === 1) {
    if (t === 'TERMS_BREACH') return 'netPayable';
    if (t === 'CONTRACTOR_PAYABLE_DEBIT') return 'proof1Balance2130';
    return 'proof1Balance2150';
  }
  if (proof === 2) {
    if (t === 'PAYOUT') return 'payoutAmount';
    if (t === 'OWN_FUNDS_WITHDRAWN') return 'proof2OwnFundsWithdrawn';
    if (t === 'REFUND_PAID') return 'proof2GoodwillFromCustody';
    if (t.startsWith('EXCLUDED')) return 'proof2ExcludedNonStripe';
    return 'proof2GarageTransfers';
  }
  // The ledger-side rows carry a 2210 figure, not a document's printed VAT (#584).
  // PRE_LEDGER_DOCUMENT belongs here too: its amount is the month's 2210 journal for a document
  // issued before go-live, which is exactly why it sits outside the tie-out.
  if (t === 'LEDGER_MISSING' || t === 'LEDGER_MISMATCH' || t === 'LEDGER_UNMATCHED' || t === 'PRE_LEDGER_DOCUMENT') {
    return 'proof3LedgerOutputVat';
  }
  return 'proof3DocumentVat';
}

interface FlatRow { key: string; value: unknown }

/** `{ a: 1, b: { c: 2 } }` → `[a=1, b.c=2]`; arrays are shown as their JSON. */
export function flattenSide(side: ProofSide | null | undefined, prefix = ''): FlatRow[] {
  if (!side) return [];
  const rows: FlatRow[] = [];
  for (const [k, v] of Object.entries(side)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) rows.push(...flattenSide(v as ProofSide, key));
    else rows.push({ key, value: v });
  }
  return rows;
}

function humanise(key: string): string {
  return key.replace(/\./g, ' › ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
}

function SideValue({ proof, row }: { proof: ProofNumber; row: FlatRow }) {
  const { key, value } = row;
  if (value === null || value === undefined) return <span style={{ color: 'var(--admin-text-muted)' }}>—</span>;
  if (typeof value === 'number') {
    const glossaryKey = FIELD_KEYS[proof][key];
    const isCount = /(^|\.)count$/.test(key) || /Count$/.test(key) || /Days$/.test(key);
    if (glossaryKey && isGlossaryKey(glossaryKey)) return <Money amount={value} glossaryKey={glossaryKey} format={isCount ? 'count' : 'aed'} />;
    return <span style={{ fontVariantNumeric: 'tabular-nums' }} data-figure={key}>{isCount ? String(value) : formatAed(value)}</span>;
  }
  if (typeof value === 'boolean') return <span>{value ? 'yes' : 'no'}</span>;
  if (Array.isArray(value)) return <code style={{ fontSize: '0.6875rem', whiteSpace: 'pre-wrap' }}>{JSON.stringify(value)}</code>;
  return <span>{String(value)}</span>;
}

function SideTable({ proof, title, side, testId }: { proof: ProofNumber; title: string; side: ProofSide | null; testId: string }) {
  const captions = ROW_CAPTIONS[proof] ?? {};
  const all = flattenSide(side);
  const present = new Set(all.map((r) => r.key));
  // Absorb a caption only when the figure it qualifies is actually in this side.
  const absorbed = new Set(
    Object.entries(captions).filter(([figure]) => present.has(figure)).map(([, caption]) => caption),
  );
  const values = new Map(all.map((r) => [r.key, r.value]));
  const rows = all.filter((r) => !absorbed.has(r.key));
  return (
    <div className="admin-table-wrapper">
      <table className="admin-table" data-testid={testId}>
        <thead><tr><th colSpan={2}>{title}</th></tr></thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={2} style={{ color: 'var(--admin-text-muted)', textAlign: 'center' }}>Nothing recorded</td></tr>
            : rows.map((r) => {
              const captionKey = captions[r.key];
              const raw = captionKey === undefined ? undefined : values.get(captionKey);
              const caption = raw === undefined || raw === null || raw === '' ? null : String(raw);
              return (
                <tr key={r.key}>
                  <td style={{ textTransform: 'capitalize' }}>{humanise(r.key)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <SideValue proof={proof} row={r} />
                    {caption !== null && (
                      <div
                        data-testid="row-caption"
                        data-caption-for={r.key}
                        style={{ fontSize: '0.6875rem', color: 'var(--admin-text-muted)', fontWeight: 400 }}
                      >
                        since {caption}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryCell({ label, children, tone }: { label: string; children: ReactNode; tone?: 'danger' | 'success' | 'warning' }) {
  const color = tone === 'danger' ? 'var(--admin-danger)' : tone === 'success' ? 'var(--admin-success)' : tone === 'warning' ? 'var(--admin-warning)' : 'var(--admin-text)';
  return (
    <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>{label}</div>
      <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem', color }}>{children}</div>
    </div>
  );
}

function ExceptionsTable({ proof, exceptions }: { proof: ProofNumber; exceptions: ProofException[] }) {
  return (
    <div className="admin-table-wrapper" style={{ marginTop: '1rem' }}>
      <table className="admin-table" data-testid="proof-exceptions">
        <thead>
          <tr>
            <th>Type</th>
            <th>Reference</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {exceptions.length === 0 ? (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No exceptions</td></tr>
          ) : exceptions.map((x, i) => (
            <tr key={`${x.type}-${x.ref ?? i}`} data-testid="proof-exception">
              <td><span className="admin-badge rejected">{String(x.type).replace(/_/g, ' ')}</span></td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{x.ref ?? '—'}</td>
              <td style={{ textAlign: 'right' }}><Money amount={x.amount} glossaryKey={exceptionGlossaryKey(proof, x.type)} hideTip /></td>
              <td>{x.detail ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ProofDetailProps {
  /** The run to show; `null` keeps the drawer closed. */
  runId: string | null;
  onClose: () => void;
}

/**
 * Spec 053 Flow E step 3 — "drills into exceptions". Same right-hand drawer treatment as
 * `PayoutDetail`: the run's status and variance, its stored lhs / rhs working side by side, then
 * the exception rows (the settlements, journals, payouts or documents an admin can tick off).
 */
export default function ProofDetail({ runId, onClose }: ProofDetailProps) {
  const auth = useAuth();
  const [run, setRun] = useState<ProofRunResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId || !auth.token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRun(null);
    financeApi.getProofRun(runId, auth.token)
      .then((r) => { if (!cancelled) setRun(r); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the proof run'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, auth.token]);

  useEffect(() => {
    if (!runId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [runId, onClose]);

  if (!runId) return null;

  const tone = run?.status === 'PASS' ? 'success' : run?.status === 'FAIL' ? 'danger' : 'warning';
  const ranAt = run ? new Date(run.ranAt) : null;

  return (
    <div className="admin-modal-overlay" style={{ justifyContent: 'flex-end', alignItems: 'stretch' }} onClick={onClose}>
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={run ? `Proof ${run.proof} ${PROOF_TITLES[run.proof]} for ${run.runDate}` : 'Proof run'}
        style={{
          width: '720px', maxWidth: '100vw', height: '100vh', margin: 0,
          borderRadius: '0.75rem 0 0 0.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {run ? <>Proof {run.proof} · {PROOF_TITLES[run.proof]} · {run.runDate} <ProofStatusBadge status={run.status} /></> : 'Proof run'}
          </h3>
          <button type="button" className="admin-modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>

        <div className="admin-modal-body" style={{ flex: 1 }}>
          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
          {loading && <div className="admin-loading"><div className="admin-loading-spinner"></div></div>}

          {run && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginBottom: '0.75rem' }} data-testid="proof-meta">
                Ran {ranAt && !Number.isNaN(ranAt.getTime()) ? `${RAN_AT.format(ranAt)} (Dubai)` : run.ranAt}
                {' · '}{run.exceptions.length} exception{run.exceptions.length === 1 ? '' : 's'}
                {run.status === 'INCONCLUSIVE' && (
                  <div className="admin-info-banner warning" role="status" style={{ marginTop: '0.5rem' }}>
                    <strong>Inconclusive:</strong>{' '}
                    {(run.reason ? REASON_TEXT[run.reason] ?? run.reason : null)
                      ?? 'the proof could not read one of its sources (billing or Stripe snapshot) — no alert fired.'}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }} data-testid="proof-summary">
                <SummaryCell label="Result" tone={tone}><ProofStatusBadge status={run.status} /></SummaryCell>
                <SummaryCell label="Variance" tone={tone}>
                  <Money amount={run.variance} glossaryKey={PROOF_VARIANCE_KEY[run.proof]} />
                </SummaryCell>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
                <SideTable proof={run.proof} title="Left-hand side (ledger)" side={run.lhs} testId="proof-lhs" />
                <SideTable proof={run.proof} title="Right-hand side (source records)" side={run.rhs} testId="proof-rhs" />
              </div>

              <ExceptionsTable proof={run.proof} exceptions={run.exceptions} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
